import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple, DeviceMobile, CheckCircle, Share, DotsThreeVertical, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * v1.27.72 — Install App card for Settings pages.
 *
 * Provides a persistent, always-visible way to install the PWA regardless
 * of whether Chrome's automatic `beforeinstallprompt` banner has fired
 * yet. Detects install state and shows the right guidance:
 *
 *   • Already installed (standalone)          → confirmation chip
 *   • iOS Safari                              → Share → Add to Home Screen
 *   • Android/Desktop Chrome + prompt ready   → live "Install App" button
 *   • Android Chrome, prompt not ready yet    → 3-dot menu → "Install app"
 *   • Unsupported browser                     → open in Chrome instructions
 *
 * Also fires the queued `beforeinstallprompt` if one was captured earlier
 * by index.js into `window.__deferredInstallPrompt`.
 */
export default function InstallAppCard() {
    const [deferred, setDeferred] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [platform, setPlatform] = useState("unknown"); // 'ios-safari' | 'ios-other' | 'android-chrome' | 'desktop-chrome' | 'other'

    useEffect(() => {
        const ua = window.navigator.userAgent || "";
        const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isSafari = isIos && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
        const isAndroid = /Android/.test(ua);
        const isChrome = /Chrome|CriOS/.test(ua) && !/Edg|OPR/.test(ua);
        const standalone =
            window.matchMedia("(display-mode: standalone)").matches ||
            window.navigator.standalone === true;

        if (standalone) { setInstalled(true); return; }

        if (isIos) {
            setPlatform(isSafari ? "ios-safari" : "ios-other");
        } else if (isAndroid && isChrome) {
            setPlatform("android-chrome");
        } else if (!isIos && isChrome) {
            setPlatform("desktop-chrome");
        } else {
            setPlatform("other");
        }

        // Pick up any event captured before this component mounted.
        if (window.__deferredInstallPrompt) setDeferred(window.__deferredInstallPrompt);
        const onAvailable = () => {
            if (window.__deferredInstallPrompt) setDeferred(window.__deferredInstallPrompt);
        };
        const onInstalled = () => { setInstalled(true); setDeferred(null); };
        window.addEventListener("micm-install-available", onAvailable);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("micm-install-available", onAvailable);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const doInstall = async () => {
        if (!deferred) return;
        try {
            deferred.prompt();
            const { outcome } = await deferred.userChoice;
            window.__deferredInstallPrompt = null;
            setDeferred(null);
            if (outcome === "accepted") {
                toast.success("Installing MICM to your home screen…");
            } else {
                toast.info("Install cancelled. You can try again from Settings anytime.");
            }
        } catch (e) {
            toast.error("Couldn't start the install prompt on this device.");
        }
    };

    // Reset localStorage dismiss so the login-banner reappears next visit.
    const resetBanner = () => {
        try {
            localStorage.removeItem("micm_install_dismissed_at");
            localStorage.removeItem("micm-ios-install-hint-dismissed");
            toast.success("Install banner will show again on your next screen.");
        } catch {}
    };

    return (
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="install-app-card">
            <h2 className="mb-3 flex items-center gap-2 font-display text-base font-semibold text-slate-800">
                <DeviceMobile size={18} weight="duotone" className="text-slate-500" />
                Install App
            </h2>

            {installed ? (
                <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="install-status-installed">
                    <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" />
                    <div>
                        <div className="text-sm font-semibold text-emerald-900">MICM is installed on this device</div>
                        <div className="mt-0.5 text-xs text-emerald-800">You're using the app in standalone mode. Enjoy the native-like experience.</div>
                    </div>
                </div>
            ) : platform === "ios-safari" ? (
                <div className="space-y-2 text-sm text-slate-700" data-testid="install-status-ios-safari">
                    <p>iOS doesn't offer an in-page install button. Add MICM to your Home Screen in two taps:</p>
                    <ol className="list-decimal space-y-1 pl-5 text-[13px]">
                        <li>Tap the <Share size={13} className="inline align-middle text-blue-600" weight="fill" /> <b>Share</b> button in Safari's toolbar.</li>
                        <li>Scroll and choose <b>"Add to Home Screen"</b>.</li>
                        <li>Tap <b>Add</b> — the MICM icon will appear on your Home Screen like a normal app.</li>
                    </ol>
                </div>
            ) : platform === "ios-other" ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="install-status-ios-other">
                    <Info size={16} weight="fill" className="mr-1 inline align-text-bottom" />
                    On iPhone / iPad, install only works from the <b>Safari</b> browser. Open <span className="font-mono text-xs">teleadmin.in</span> in Safari, then come back to Settings → Install App.
                </div>
            ) : deferred ? (
                <div className="flex flex-wrap items-center justify-between gap-3" data-testid="install-status-ready">
                    <div className="min-w-0">
                        <div className="text-sm font-semibold text-slate-800">Install MICM on this device</div>
                        <div className="mt-0.5 text-xs text-slate-500">One-tap install. Adds an app icon to your home screen, opens full-screen, works offline for viewing.</div>
                    </div>
                    <Button
                        data-testid="install-app-btn"
                        onClick={doInstall}
                        className="bg-blue-600 text-white hover:bg-blue-700"
                    >
                        <DownloadSimple size={16} weight="bold" className="mr-2" />
                        Install App
                    </Button>
                </div>
            ) : platform === "android-chrome" ? (
                <div className="space-y-3 text-sm text-slate-700" data-testid="install-status-android-manual">
                    <p>Chrome hasn't offered a direct install button yet on this device. You can still install manually — it takes two taps:</p>
                    <ol className="list-decimal space-y-1 pl-5 text-[13px]">
                        <li>Tap the <DotsThreeVertical size={16} weight="bold" className="inline align-middle" /> <b>menu</b> icon (top-right of Chrome).</li>
                        <li>Choose <b>"Install app"</b> or <b>"Add to Home screen"</b>.</li>
                        <li>Confirm <b>Install</b> — MICM will open like a normal app from your home screen.</li>
                    </ol>
                    <div className="rounded-md bg-slate-50 p-2 text-[12px] text-slate-500">
                        Chrome shows the in-page install button only after a few visits. Once it does, this section will switch to a one-tap button.
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={resetBanner}
                        data-testid="install-reset-banner"
                        className="text-xs"
                    >
                        Show install banner again
                    </Button>
                </div>
            ) : platform === "desktop-chrome" ? (
                <div className="space-y-2 text-sm text-slate-700" data-testid="install-status-desktop-manual">
                    <p>To install MICM as a desktop app:</p>
                    <ol className="list-decimal space-y-1 pl-5 text-[13px]">
                        <li>Look for the <DownloadSimple size={14} weight="bold" className="inline align-middle" /> install icon at the right end of the address bar.</li>
                        <li>Or open Chrome menu <DotsThreeVertical size={14} weight="bold" className="inline align-middle" /> → <b>"Install MICM…"</b>.</li>
                    </ol>
                </div>
            ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="install-status-unsupported">
                    <Info size={16} weight="fill" className="mr-1 inline align-text-bottom" />
                    Your current browser doesn't support Progressive Web App installs. Please open <span className="font-mono text-xs">teleadmin.in</span> in <b>Chrome</b> (Android / Desktop) or <b>Safari</b> (iPhone / iPad) to install.
                </div>
            )}
        </section>
    );
}
